import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography } from '@mui/material';
import logo from '../assets/swise_black.png';
import LeftSide from '../components/LeftSide'
import { createUser } from '../api/userAPI'
import { useTranslation } from 'react-i18next';

const COMPANY_ID = process.env.REACT_APP_COMPANY_ID ?? '';

const Signup = () => {
  const navigate = useNavigate();
  const signupCardRef = useRef<HTMLDivElement>(null);
  const [signupHeight, setLoginHeight] = useState<number | undefined>(undefined);
  const [serverError, setServerError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    email: false,
    emailFormat: false,
  });

  const [language, setLanguage] = useState<string>(() => {
    const stored = localStorage.getItem('language');
    return stored ? stored.toUpperCase() : 'EN';
  });

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);


  useEffect(() => {
    if (!signupCardRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLoginHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(signupCardRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  const handleSubmit = async () => {

    const normalizedEmail = email.trim().toLowerCase();

    const hasErrors = {
      name: name.trim() === '',
      surname: surname.trim() === '',
      company: company.trim() === '',
      email: normalizedEmail === '',
      emailFormat: !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail),
    };

    setErrors(hasErrors);

    const isValid = !hasErrors.name && !hasErrors.surname && !hasErrors.company && !hasErrors.email && !hasErrors.emailFormat;

    if (isValid) {
      try {
        const response = await createUser(name, surname, company, normalizedEmail, COMPANY_ID, language, true);
        console.log('[handleSubmit] User created successfully:', response);
        navigate('/signupmessage');
      } catch (err: any) {
        console.error('[handleSubmit] Error while creating User:', err.message);
        setServerError(err.message || 'Error while creating User.');
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Left Side */}
      <Box
        sx={{
          width: '60%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LeftSide minHeight={signupHeight} />
      </Box>

      {/* Right Side */}
      <Box
        sx={{
          width: '40%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          ref={signupCardRef}
          sx={{
            width: '80%',
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img
            src={logo}
            alt="Swise Logo"
            style={{
              width: '100px',
              height: 'auto',
              marginBottom: '56px',
              alignSelf: 'flex-start',
            }}
          />

          <Typography
            gutterBottom
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 'bold',
              fontSize: '25px',
              color: 'black',
            }}
          >
             {t('welcome')}
          </Typography>
          <Typography
            gutterBottom
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 'normal',
              fontSize: '22px',
              color: 'grey',
            }}
          >
            {t('enterDetailsExtended')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <TextField
                label={t('name')}
                fullWidth
                margin="normal"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={errors.name}
                helperText={errors.name ? t('nameRequired') : ''}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                label={t('surname')}
                fullWidth
                margin="normal"
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                error={errors.surname}
                helperText={errors.surname ? t('surnameRequired') : ''}
              />
            </Box>
          </Box>

          <TextField
            label={t('company')}
            type="company"
            fullWidth
            margin="normal"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            error={errors.email || errors.emailFormat}
            helperText={errors.surname ? t('companyRequired') : ''}
          />
          <TextField
            label={t('email')}
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={errors.email || errors.emailFormat}
            helperText={
              errors.email
                ? t('emailRequired')
                : errors.emailFormat
                ? t('emailFormat')
                : ''
            }
          />
          {serverError && (
            <Typography sx={{ color: 'red', mt: 1 }}>
              {serverError}
            </Typography>
          )}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            sx={{
              mt: 2,
              height: '45px', // 56px == standard
              backgroundColor: '#73AF73',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#A8CFA9',
              },
            }}
          >
            {t('request')}
          </Button>

          {/* Section under the button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mt: 3,
            }}
          >
            <Typography sx={{ fontSize: '16px', color: 'grey' }}>
              {t('haveAccess')}{' '}
              <Typography
                component={Link}
                to="/"
                sx={{
                  color: '#A8CFA9',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1976d2',
                  },
                }}
              >
                {t('signIn')}
              </Typography>
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <Typography
                sx={{
                  fontSize: '16px',
                  color: 'grey',
                }}
              >
                {t('havingProblems')}
              </Typography>
              <Typography
                component="a"
                href="mailto:it@swise.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '16px',
                  color: '#A8CFA9',
                  mt: 0,
                  mb: 15,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#1976d2',
                  },
                }}
              >
                {t('contactSupport')}
              </Typography>
            </Box>
          </Box>
          <Typography
            sx={{
              fontSize: '15px',
              color: 'grey',
              mt: 3,
              alignSelf: 'flex-start',
            }}
          >
            © 2025 Swise AG
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Signup;